import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Onboarding, { OnboardingFormValues } from '../components/Onboarding';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { useAuth } from '../context/AuthContext';
import { authClient } from '../lib/auth';
import { useApiClient, BikeProfileUpdate } from '../services/apiClient';
import { BikeType, RidingStyle, RidingExperience, Flexibility } from '../constants/bikeConfig';
import { captureEvent } from '../lib/analytics';
import { buildPainDetail, parsePainDetail, heightToCm, weightToKg, cmToHeight, kgToWeight } from './onboardingMapping';

const LOCAL_STORAGE_KEY = 'bikendure_bike_profile';

// ── Type mappings: frontend enums → backend enum values ───────────────────────

const BIKE_TYPE_MAP: Record<string, BikeProfileUpdate['bike_type']> = {
  road:      'road',
  gravel:    'gravel',
  trekking:  'trekking',
  mountain:  'mountain',
  city:      'trekking',
  triathlon: 'time-trial',
};

const EXPERIENCE_MAP: Record<RidingExperience, BikeProfileUpdate['experience_level']> = {
  beginner:     'beginner',
  intermediate: 'intermediate',
  experienced:  'advanced',
};

const FLEXIBILITY_MAP: Record<Flexibility, BikeProfileUpdate['flexibility_level']> = {
  good:    'high',
  average: 'moderate',
  limited: 'low',
};

const POSITION_MAP: Record<RidingStyle, BikeProfileUpdate['position_preference']> = {
  performance: 'aggressive',
  balanced:    'moderate',
  comfort:     'relaxed',
};

// ── Reverse maps: backend enum values → frontend types ────────────────────────

const BIKE_TYPE_REVERSE: Record<string, BikeType> = {
  road:        'road',
  mountain:    'mountain',
  gravel:      'gravel',
  trekking:    'trekking',
  'time-trial':'triathlon',
};

const EXPERIENCE_REVERSE: Record<string, RidingExperience> = {
  beginner:     'beginner',
  intermediate: 'intermediate',
  advanced:     'experienced',
};

const FLEXIBILITY_REVERSE: Record<string, Flexibility> = {
  high:     'good',
  moderate: 'average',
  low:      'limited',
};

const POSITION_REVERSE: Record<string, RidingStyle> = {
  aggressive: 'performance',
  moderate:   'balanced',
  relaxed:    'comfort',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function toBikeProfilePayload(values: OnboardingFormValues): BikeProfileUpdate {
  return {
    bike_type:           BIKE_TYPE_MAP[values.bikeType],
    experience_level:    EXPERIENCE_MAP[values.experience],
    flexibility_level:   FLEXIBILITY_MAP[values.flexibility],
    position_preference: POSITION_MAP[values.ridingStyle],
    pain_detail:         buildPainDetail(values.pains, values.painFree, values.painOther),
    height_cm:           heightToCm(values.height, values.units),
    weight_kg:           weightToKg(values.weight, values.units),
    units:               values.units,
  };
}

/**
 * The onboarding account card asks for email + password only (per the design),
 * but Better Auth requires a name. Derive a readable one from the local part;
 * the rider can change it in settings.
 */
function nameFromEmail(email: string): string {
  const local = email.trim().split('@')[0] ?? '';
  const pretty = local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
    .trim();
  return pretty || 'Rider';
}

const OnboardingPage: React.FC = () => {
  const { isSignedIn, isLoaded, user } = useAuth();
  const api = useApiClient();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [initialValues, setInitialValues] = useState<Partial<OnboardingFormValues> | undefined>(undefined);

  useEffect(() => {
    captureEvent('onboarding_started');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load from localStorage immediately (works for anonymous AND as fallback while auth loads)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as OnboardingFormValues;
        setInitialValues(parsed);
      }
    } catch { /* corrupt localStorage — ignore */ }
    setReady(true);
  }, []);

  // Once auth resolves and user is signed in, upgrade to backend data
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    Promise.all([api.getBikeProfile(), api.getSettings().catch(() => undefined)])
      .then(([profile, settings]) => {
        const units = settings?.units ?? 'metric';
        const { pains, painFree, painOther } = parsePainDetail(profile.pain_detail ?? null);
        setInitialValues({
          bikeType:    profile.bike_type           ? (BIKE_TYPE_REVERSE[profile.bike_type]           ?? 'road')         : undefined,
          ridingStyle: profile.position_preference ? (POSITION_REVERSE[profile.position_preference]  ?? 'balanced')     : undefined,
          experience:  profile.experience_level    ? (EXPERIENCE_REVERSE[profile.experience_level]   ?? 'intermediate') : undefined,
          flexibility: profile.flexibility_level   ? (FLEXIBILITY_REVERSE[profile.flexibility_level] ?? 'average')      : undefined,
          units,
          height:      cmToHeight(profile.height_cm ?? null, units),
          weight:      kgToWeight(profile.weight_kg ?? null, units),
          pains,
          painFree,
          painOther,
        });
      })
      .catch(() => {});
  }, [isLoaded, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;

  const handleSave = async (values: OnboardingFormValues): Promise<void> => {
    if (isSignedIn) {
      await api.saveBikeProfile(toBikeProfilePayload(values));
      // The backend now holds the newer copy; a leftover anonymous snapshot
      // would be re-synced over it by DashboardPage on the next visit.
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
    }
  };

  /**
   * Review-step signup for anonymous riders. Creates the account, then attaches
   * the profile they just filled in. We call `saveBikeProfile` directly rather
   * than going through `handleSave` — `isSignedIn` comes from the session hook
   * and has not re-rendered yet at this point.
   */
  const handleCreateAccount = async (
    creds: { email: string; password: string },
    values: OnboardingFormValues,
  ): Promise<void> => {
    const result = await authClient.signUp.email({
      name: nameFromEmail(creds.email),
      email: creds.email,
      password: creds.password,
    });

    if (result.error) {
      const message = result.error.message ?? '';
      // Better Auth answers a duplicate email with 422 (not 409) — key off the
      // stable error code, and fall back to the message for older versions.
      const taken =
        result.error.code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL' ||
        /already exists|already registered|taken/i.test(message);
      throw new Error(
        taken
          ? 'An account with that email already exists — sign in instead.'
          : message || 'Account could not be created. Please try again.',
      );
    }

    captureEvent('user_signed_up');

    // Session cookie is set (requireEmailVerification is off), so the API client
    // can mint a token. A failure here must not lose the answers.
    try {
      await api.saveBikeProfile(toBikeProfilePayload(values));
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
    }
  };

  const handleComplete = (_values: OnboardingFormValues) => {
    captureEvent('onboarding_completed');
    navigate('/video-upload');
  };

  return (
    <Onboarding
      onComplete={handleComplete}
      onSave={handleSave}
      onCreateAccount={handleCreateAccount}
      socialSignup={(persistAnswers) => (
        <GoogleAuthButton
          mode="sign-up"
          // Both OAuth outcomes land back here: a brand-new account follows
          // Better Auth's fixed newUserCallbackURL ('/onboarding'), and an
          // existing Google account follows this returnUrl. The resume
          // snapshot puts the rider back on the review step, now signed in.
          returnUrl="/onboarding"
          onBeforeRedirect={persistAnswers}
        />
      )}
      initialValues={initialValues}
      signedIn={isSignedIn}
      identity={user ? { name: user.name, email: user.email } : undefined}
    />
  );
};

export default OnboardingPage;
